import { queryClient } from '@lib/queryClient';
import { useSyncExternalStore } from 'react';

type Listener = () => void;

const listeners = new Set<Listener>();
const secretFields = ['token', 'refreshToken', 'accessToken'] as const;
const tokenStorageKey = 'tokens';

interface StoredTokens {
  accessToken: string;
  refreshToken: string | null;
}

let loggedIn = false;

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const withoutSecrets = (value: object): Record<string, unknown> => {
  const copy = { ...(value as Record<string, unknown>) };
  for (const field of secretFields) {
    delete copy[field];
  }
  return copy;
};

const tokenSource = (body: unknown): Record<string, unknown> | null => {
  const record = asRecord(body);
  if (!record) return null;
  const nested = asRecord(record.data);
  if (
    nested &&
    (typeof nested.token === 'string' || typeof nested.accessToken === 'string')
  ) {
    return nested;
  }
  return record;
};

export const readStoredTokens = (): StoredTokens | null => {
  const raw = localStorage.getItem(tokenStorageKey);
  if (!raw) return null;
  try {
    const parsed = asRecord(JSON.parse(raw));
    if (!parsed || typeof parsed.accessToken !== 'string' || !parsed.accessToken) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken:
        typeof parsed.refreshToken === 'string' ? parsed.refreshToken : null,
    };
  } catch {
    return null;
  }
};

export const storeAuthTokens = (body: unknown): string | null => {
  const source = tokenSource(body);
  if (!source) return null;
  const accessToken = source.token ?? source.accessToken;
  if (typeof accessToken !== 'string' || accessToken.length === 0) return null;
  const refreshToken = source.refreshToken;
  const tokens: StoredTokens = {
    accessToken,
    refreshToken: typeof refreshToken === 'string' ? refreshToken : null,
  };
  localStorage.setItem(tokenStorageKey, JSON.stringify(tokens));
  return accessToken;
};

export const persistUser = (user: object): void => {
  storeAuthTokens(user);
  localStorage.setItem('user', JSON.stringify(withoutSecrets(user)));
};

export const getIsLoggedIn = (): boolean => {
  return loggedIn;
};

export const setLoggedIn = (value: boolean): void => {
  loggedIn = value;
  emit();
};

export const restoreSession = async (): Promise<void> => {
  const raw = localStorage.getItem('user');
  if (raw) {
    try {
      const user = JSON.parse(raw) as object;
      localStorage.setItem('user', JSON.stringify(withoutSecrets(user)));
      storeAuthTokens(user);
    } catch {
      localStorage.removeItem('user');
    }
  }
  loggedIn = Boolean(readStoredTokens()?.accessToken);
  emit();
};

export const endSession = (): void => {
  localStorage.removeItem(tokenStorageKey);
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
  Object.keys(localStorage)
    .filter((key) => key.startsWith('redux-'))
    .forEach((key) => localStorage.removeItem(key));
  loggedIn = false;
  queryClient.clear();
  emit();
};

export const useSession = (): boolean => {
  return useSyncExternalStore(subscribe, getIsLoggedIn, () => false);
};
